// Bots keep the existing /agents and /g routes so shared links and category SSR remain compatible.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, RefreshControl, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import useStartAgentWorkChat from '@/hooks/useStartAgentWorkChat';
import { useAgentStore, useAuthStore, useModalStore, useToastStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import StatusContainer from '@/components/StatusContainer';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import MetaHead from '@/components/MetaHead';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import { normalizeCategoryPageCopy, buildCategoryFaqBlock } from '@/utils/pageCopyBlocks';
import CatalogBrowseShell, { CATALOG_VIEW_MODES } from '@/components/Catalog/CatalogBrowseShell';
import CatalogSortDropdown from '@/components/Catalog/CatalogSortDropdown';
import CatalogMobileToolbar from '@/components/Catalog/CatalogMobileToolbar';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import AgentCard from '@/components/AgentCard';
import AgentListRow, { AGENT_LIST_COLS } from '@/components/Agents/AgentListRow';
import AgentTile from '@/components/Agents/AgentTile';
import AgentCategoryChips from '@/components/Agents/AgentCategoryChips';
import CatalogDiscoveryHero from '@/components/Catalog/CatalogDiscoveryHero';
import AgentsIntroHero from '@/components/Agents/AgentsIntroHero';
import BotsPersonalSection from '@/components/Agents/BotsPersonalSection';
import { flattenAgentsFromCategories, attachAgentCategory } from '@/utils/agent_catalog';
import useCopyAgentLink from '@/hooks/useCopyAgentLink';

const CATEGORY_PRIORITY = { 'content-creation': 0, homeschooling: 1, education: 2 };
const compareCategories = (a, b) =>
  (CATEGORY_PRIORITY[a.slug] ?? 3) - (CATEGORY_PRIORITY[b.slug] ?? 3)
  || (a.order ?? 999) - (b.order ?? 999)
  || a.name.localeCompare(b.name);

/** Shared hero subtitle when a category has no short description. */
const AGENTS_DISCOVERY_FALLBACK_SUBTITLE =
  'Find a bot for the job, or create one with your own instructions.';

/** Discovery chips under the search field — neutral (no per-chip rainbow). */
const AGENT_TRY_CHIPS = [
  { label: 'Coding', query: 'code', icon: 'code' },
  { label: 'Writing', query: 'write', icon: 'pen-nib' },
  { label: 'Research', query: 'research', icon: 'magnifying-glass' },
  { label: 'Finance', query: 'finance', icon: 'chart-line' },
  { label: 'Productivity', query: 'productivity', icon: 'list-check' },
  { label: 'Creative', query: 'creative', icon: 'palette' },
];

const SEARCH_DEBOUNCE_MS = 500;

/** Persisted before in-app category navigations; restored after URL change (web), matching /tasks. */
const AGENTS_CATALOG_SCROLL_KEY = 'gab_agents_catalog_scroll_y';

function persistAgentsCatalogScroll(scrollYRef) {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      AGENTS_CATALOG_SCROLL_KEY,
      String(Math.max(0, Math.floor(scrollYRef.current))),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

const SECTION_PREVIEW = 10;
/** Tiles per category section on single-column layouts. */
const XS_SECTION_TILES = 3;
const TILE_GAP = 12;

function agentRecencyTs(agent) {
  return new Date(agent?.createdAt || agent?.updatedAt || 0).getTime();
}

/** Stable sort for catalog grids/lists (name + recency). */
function sortAgents(list, sortId) {
  if (!Array.isArray(list) || list.length === 0) return list;
  const arr = [...list];
  switch (sortId) {
    case 'nameDesc':
      return arr.sort((a, b) => String(b?.name || '').localeCompare(String(a?.name || ''), undefined, { sensitivity: 'base' }));
    case 'newest':
      return arr.sort((a, b) => agentRecencyTs(b) - agentRecencyTs(a));
    case 'oldest':
      return arr.sort((a, b) => agentRecencyTs(a) - agentRecencyTs(b));
    case 'nameAsc':
    default:
      return arr.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' }));
  }
}

const listColumnStyle = (c) => {
  const base = { flexDirection: 'row', alignItems: 'center' };
  if (c.flex) return { ...base, flex: c.flex, minWidth: c.minWidth || 0, paddingRight: 12 };
  return { ...base, width: c.width };
};

function translateAgentColumnLabel(id, tr) {
  switch (id) {
    case 'name': return tr('pages.agents.colAgent') || 'Agent';
    case 'favorite': return tr('pages.agents.colFavorite') || 'Save';
    case 'chat': return tr('pages.agents.colChat') || 'Chat';
    default: return id;
  }
}

// ---------------------------------------------------------------------------
// AgentsPage — also exported so /agents/category/[slug].jsx can render the
// same UI in a category-specific mode without duplicating wiring.
// ---------------------------------------------------------------------------
export const AgentsPage = React.memo(({ categorySlug: categorySlugProp = null } = {}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const { isXS, isSM, isMD, isXL } = useResponsiveness();
  const router = useRouter();
  const { category: urlCategory } = useLocalSearchParams();

  // /agents/category/<slug> wraps this component and forwards the slug
  // as a prop. The prop wins over `?category=<slug>` so the URL form
  // can't override the path-based route.
  const effectiveUrlCategory = categorySlugProp || urlCategory;
  const activeCategory = effectiveUrlCategory || 'all';

  // ── store wiring ──────────────────────────────────────────────────────
  const categories                 = useAgentStore(s => s.categories);
  const categoriesWithAgentIds     = useAgentStore(s => s.categoriesWithAgentIds);
  const categoryAgentIds           = useAgentStore(s => s.categoryAgentIds);
  const searchResultIds            = useAgentStore(s => s.searchResultIds);
  const myAgentIds                 = useAgentStore(s => s.myAgentIds);
  const agentsById                 = useAgentStore(s => s.agents);
  const isLoadingAgentCategories   = useAgentStore(s => s.isLoadingAgentCategories);
  const isLoadingCategory          = useAgentStore(s => s.isLoadingCategory);
  const isLoadingAgentSearch       = useAgentStore(s => s.isLoadingAgentSearch);
  const isLoadingMyAgents          = useAgentStore(s => s.isLoadingMyAgents);
  const categoriesError            = useAgentStore(s => s.categoriesError);
  const categoryError              = useAgentStore(s => s.categoryError);
  const searchError                = useAgentStore(s => s.searchError);
  const myAgentsError              = useAgentStore(s => s.myAgentsError);
  const categoryHasMore            = useAgentStore(s => s.pagination.category?.hasMore);
  const categoryPage               = useAgentStore(s => s.pagination.category?.page);
  const myAgentsHasMore            = useAgentStore(s => s.pagination.myAgents?.hasMore);
  const myAgentsPage               = useAgentStore(s => s.pagination.myAgents?.page);
  const myAgentsTotal              = useAgentStore(s => s.pagination.myAgents?.total);

  const fetchAgentCategories       = useAgentStore(s => s.fetchAgentCategories);
  const fetchCategoryAgents        = useAgentStore(s => s.fetchCategoryAgents);
  const searchAgents               = useAgentStore(s => s.searchAgents);
  const fetchMyAgents              = useAgentStore(s => s.fetchMyAgents);
  const ensureAllMyAgents          = useAgentStore(s => s.ensureAllMyAgents);
  const resetSearch                = useAgentStore(s => s.resetSearch);
  const resetCategory              = useAgentStore(s => s.resetCategory);
  const getAgentById               = useAgentStore(s => s.getAgentById);
  const getAgentCategoryById       = useAgentStore(s => s.getAgentCategoryById);

  const userId                     = useAuthStore(s => s.user?.id);
  const hasUser                    = Boolean(userId);
  const openModal                  = useModalStore(s => s.actions.openModal);
  const requireAuth                = useRequireAuth();
  const startAgentWorkChat         = useStartAgentWorkChat();
  const { copyAgentRegistry }     = useCopyAgentLink();

  // ── local UI state ────────────────────────────────────────────────────
  const [query, setQuery]               = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef                     = useRef(null);
  const scrollViewRef                   = useRef(null);
  const scrollYRef                      = useRef(0);
  const [refreshing, setRefreshing]     = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [catalogView, setCatalogView]   = useState(CATALOG_VIEW_MODES.GRID);
  const [agentSort, setAgentSort]       = useState('nameAsc');
  // Track scroll Y so the sticky search bar can pick up a faint border
  // bottom once the editorial header has scrolled away.
  const isSearching = !!debouncedQuery;

  // ── data bootstrap ────────────────────────────────────────────────────
  useEffect(() => { fetchAgentCategories(); }, [fetchAgentCategories]);

  useEffect(() => {
    if (activeCategory === 'all') {
      // Clear any deep-category buffer so the next deep-jump starts at
      // page 1 with no leftover state from a previous category.
      resetCategory();
      // Pull the viewer's own agents so the All view can surface a
      // "My Agents" section pinned above the agents we offer.
      if (hasUser) fetchMyAgents();
    } else if (activeCategory === 'my-agents') {
      // Personal "My Agents" scope only makes sense for signed-in users;
      // anonymous visitors see the sign-in CTA in the empty state instead
      // of a 401 from `/api/agents/mine`.
      if (hasUser) fetchMyAgents();
    } else {
      fetchCategoryAgents(activeCategory);
    }
  }, [activeCategory, hasUser, resetCategory, fetchMyAgents, fetchCategoryAgents]);

  // Reset search on category change so users don't get a stale
  // "showing X results for Y" surfacing in a different scope.
  useEffect(() => {
    resetSearch();
    setQuery('');
    setDebouncedQuery('');
  }, [activeCategory, resetSearch]);

  // Debounced server search — local state mirrors the latest committed
  // query so we can render "showing search results for…" copy without
  // jittering on every keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setDebouncedQuery('');
      resetSearch();
      return;
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query.trim());
      searchAgents(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, resetSearch, searchAgents]);

  const catalogScrollRestoreKey = activeCategory;
  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
    let raw;
    try {
      raw = sessionStorage.getItem(AGENTS_CATALOG_SCROLL_KEY);
    } catch {
      return;
    }
    if (raw == null) return;
    try {
      sessionStorage.removeItem(AGENTS_CATALOG_SCROLL_KEY);
    } catch {
      /* ignore */
    }
    const y = parseInt(raw, 10);
    if (!Number.isFinite(y) || y < 0) return;
    requestAnimationFrame(() => {
      scrollViewRef.current?.scrollTo?.({ y, animated: false });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed restore only
  }, [catalogScrollRestoreKey]);

  useEffect(() => () => {
    resetSearch();
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, [resetSearch]);

  // ── derived data ──────────────────────────────────────────────────────
  // Resolve agent objects from id arrays.
  const myAgents = useMemo(
    () => myAgentIds.map(id => agentsById[id]).filter(Boolean),
    [myAgentIds, agentsById],
  );
  const categoryAgents = useMemo(
    () => categoryAgentIds.map(getAgentById).filter(Boolean),
    [categoryAgentIds, getAgentById],
  );
  const searchResults = useMemo(
    () => searchResultIds.map(getAgentById).filter(Boolean),
    [searchResultIds, getAgentById],
  );

  const flatAgents = useMemo(
    () => flattenAgentsFromCategories(categoriesWithAgentIds, getAgentById),
    [categoriesWithAgentIds, getAgentById],
  );

  const agentCategoryById = useMemo(() => {
    const map = new Map();
    (categoriesWithAgentIds || []).forEach((cat) => {
      const meta = getAgentCategoryById(cat.id);
      if (!meta) return;
      (cat.agentIds || []).forEach((id) => {
        if (id && !map.has(id)) map.set(id, meta);
      });
    });
    return map;
  }, [categoriesWithAgentIds, getAgentCategoryById]);

  // The /agents landing always keeps the intro hero (with "Your bots") mounted:
  // category chips and search only swap the list underneath so the page never
  // jumps. The SEO /agents/category/<slug> routes show the compact discovery
  // header instead, collapsing to a bare search field mid-search.
  const showIntroHero = !categorySlugProp;
  const showAgentsDiscoveryHero = !showIntroHero && !isSearching;
  const isEditorialAll = activeCategory === 'all' && !isSearching;
  const heroPadX = isXS || isSM ? 15 : 20;

  const categoryList = useMemo(() => Object.values(categories)
    .map(c => ({
      id: c.slug,
      slug: c.slug,
      name: c.name,
      description: c.description,
      order: c?.order ?? 999,
    }))
    .sort(compareCategories),
    [categories],
  );

  // Sections for the All view. Skip empty categories.
  const sectionsToRender = useMemo(() => {
    if (isSearching || activeCategory !== 'all') return [];
    return (categoriesWithAgentIds || [])
      .map((cat) => {
        const meta = getAgentCategoryById(cat.id);
        if (!meta) return null;
        const agents = [];
        for (const id of cat.agentIds || []) {
          const agent = getAgentById(id);
          if (!agent) continue;
          agents.push(attachAgentCategory(agent, meta));
        }
        if (agents.length === 0) return null;
        const total = Number(meta?.stats?.agentCount);
        return {
          id: meta.id,
          slug: meta.slug,
          name: meta.name,
          order: meta.order,
          agents,
          total: Number.isFinite(total) && total > 0 ? total : agents.length,
        };
      })
      .filter(Boolean)
      .sort(compareCategories);
  }, [categoriesWithAgentIds, isSearching, activeCategory, getAgentById, getAgentCategoryById]);

  const agentSortOptions = useMemo(() => [
    { id: 'nameAsc', label: t('pages.agents.sortNameAZ') || 'Name A–Z', icon: 'arrow-down-a-z', iosIcon: 'textformat.abc' },
    { id: 'nameDesc', label: t('pages.agents.sortNameZA') || 'Name Z–A', icon: 'arrow-up-a-z', iosIcon: 'textformat.abc' },
    { id: 'newest', label: t('pages.agents.sortNewest') || 'Newest', icon: 'sparkles', iosIcon: 'sparkles' },
    { id: 'oldest', label: t('pages.agents.sortOldest') || 'Oldest', icon: 'clock', iosIcon: 'clock' },
  ], [t]);

  const sortedSearchResults = useMemo(
    () => sortAgents(searchResults, agentSort),
    [searchResults, agentSort],
  );
  const sortedMyAgents = useMemo(
    () => sortAgents(myAgents, agentSort),
    [myAgents, agentSort],
  );
  const sortedCategoryAgents = useMemo(
    () => sortAgents(categoryAgents, agentSort),
    [categoryAgents, agentSort],
  );
  const sortedSectionsToRender = useMemo(
    () => sectionsToRender.map((sec) => ({ ...sec, agents: sortAgents(sec.agents, agentSort) })),
    [sectionsToRender, agentSort],
  );

  const activeCategoryDetails = useMemo(() => {
    if (activeCategory === 'all' || activeCategory === 'my-agents') return null;
    // Look up by slug since the URL uses the slug, not the mongo id.
    return Object.values(categories).find(c => c.slug === activeCategory) || null;
  }, [activeCategory, categories]);

  const decorateAgent = useCallback((agent) => {
    if (!agent) return agent;
    const fromMap = agentCategoryById.get(agent.id);
    if (fromMap) return attachAgentCategory(agent, fromMap);
    if (activeCategoryDetails) return attachAgentCategory(agent, activeCategoryDetails);
    return agent;
  }, [agentCategoryById, activeCategoryDetails]);

  const categoryPageCopyBlocks = useMemo(() => {
    if (!activeCategoryDetails) return [];
    const blocks = normalizeCategoryPageCopy(activeCategoryDetails?.meta?.pageCopy);
    const hasFaqBlock = blocks.some((b) => b?.type === 'faq');
    if (hasFaqBlock) return blocks;
    const faqTitle = activeCategoryDetails?.name
      ? `${activeCategoryDetails.name} FAQ`
      : 'Frequently asked questions';
    const faqBlock = buildCategoryFaqBlock(activeCategoryDetails?.meta?.faq, faqTitle);
    return faqBlock ? [...blocks, faqBlock] : blocks;
  }, [activeCategoryDetails]);

  // Neutral on every category — the catalog no longer color-codes categories.
  const categoryPageCopyPalette = useMemo(() => {
    const fg = colors.text_color_primary;
    return { fg, bg: `${fg}1f`, soft: `${fg}0d` };
  }, [colors]);

  const categoryMetaIntroText = useMemo(() => {
    const d = activeCategoryDetails?.description?.trim();
    if (!d) return '';
    const md = activeCategoryDetails?.meta?.description?.trim();
    if (!md || d === md) return '';
    return d;
  }, [activeCategoryDetails]);

  // Long-form SEO at bottom of /agents (mirrors category `meta.pageCopy` in seed JSON).
  const landingCatalogPageCopyBlocks = useMemo(() => {
    if (categorySlugProp || isSearching || activeCategory !== 'all') return [];
    const k = (name) => t(`pages.agents.${name}`);
    const raw = [
      {
        type: 'section',
        title: k('landingCatalogSeoSectionTitle'),
        subtitle: k('landingCatalogSeoSectionSubtitle'),
        copy: k('landingCatalogSeoSectionCopy'),
      },
      {
        type: 'ordered-list',
        title: k('landingCatalogSeoHowTitle'),
        items: [1, 2, 3, 4, 5].map((i) => k(`landingCatalogSeoHow${i}`)),
      },
      {
        type: 'unordered-list',
        variant: 'check',
        title: k('landingCatalogSeoBestTitle'),
        items: [1, 2, 3, 4, 5, 6, 7].map((i) => k(`landingCatalogSeoBest${i}`)),
      },
      {
        type: 'faq',
        title: k('landingCatalogSeoFaqTitle'),
        items: [
          { question: k('landingCatalogSeoFaq1Q'), answer: k('landingCatalogSeoFaq1A') },
          { question: k('landingCatalogSeoFaq2Q'), answer: k('landingCatalogSeoFaq2A') },
          { question: k('landingCatalogSeoFaq3Q'), answer: k('landingCatalogSeoFaq3A') },
          { question: k('landingCatalogSeoFaq4Q'), answer: k('landingCatalogSeoFaq4A') },
          { question: k('landingCatalogSeoFaq5Q'), answer: k('landingCatalogSeoFaq5A') },
        ],
      },
      {
        type: 'cards',
        title: k('landingCatalogSeoCardsTitle'),
        items: [
          { title: k('landingCatalogSeoCard1Title'), description: k('landingCatalogSeoCard1Description') },
          { title: k('landingCatalogSeoCard2Title'), description: k('landingCatalogSeoCard2Description') },
          { title: k('landingCatalogSeoCard3Title'), description: k('landingCatalogSeoCard3Description') },
        ],
      },
      {
        type: 'callout',
        title: k('landingCatalogSeoCalloutTitle'),
        copy: k('landingCatalogSeoCalloutCopy'),
      },
    ];
    return normalizeCategoryPageCopy(raw);
  }, [t, categorySlugProp, isSearching, activeCategory]);

  const landingCatalogPageCopyPalette = useMemo(() => {
    const fg = colors.text_color_primary;
    return { fg, bg: `${fg}1f`, soft: `${fg}0d` };
  }, [colors]);

  // ── handlers ──────────────────────────────────────────────────────────
  const onChangeCategory = useCallback((categoryId) => {
    if (categoryId === activeCategory) return;

    // On the landing page a category is just a filter under the fixed hero:
    // swap the query param in place so nothing remounts or scrolls.
    if (!categorySlugProp) {
      if (categoryId === 'all') resetCategory();
      router.setParams({ category: categoryId === 'all' ? undefined : categoryId });
      return;
    }

    persistAgentsCatalogScroll(scrollYRef);

    if (categoryId === 'my-agents') {
      router.push('/agents?category=my-agents');
      return;
    }
    if (categoryId === 'all') {
      resetCategory();
      router.push('/agents');
      return;
    }
    router.push(`/agents/category/${encodeURIComponent(categoryId)}`);
  }, [router, resetCategory, activeCategory, categorySlugProp]);

  const openAgentChat = useCallback((agent) => {
    startAgentWorkChat(agent, { source: 'agents_catalog' });
  }, [startAgentWorkChat]);

  const giveAgentJob = useCallback((agent) => {
    startAgentWorkChat(agent, { source: 'agents_catalog_job', requestTask: true });
  }, [startAgentWorkChat]);

  const onCreateAgent = useCallback(() => {
    requireAuth(() => openModal('CustomAgentModal'), {
      source: 'agents_create',
      title: t('pages.agents.intro.createCta'),
      subtitle: t('pages.agents.intro.signInHint'),
      icon: 'plus',
    });
  }, [requireAuth, openModal, t]);

  const agentsDiscoveryHeroProps = useMemo(() => {
    const base = {
      search: {
        value: query,
        onChangeText: setQuery,
        placeholder: t('pages.agents.searchPlaceholder'),
      },
      tryChips: AGENT_TRY_CHIPS,
      onTryQuery: (q) => setQuery(q),
    };
    if (activeCategory === 'all') {
      return {
        ...base,
        topline: t('pages.agents.heroTitle') || 'Your specialized AI workforce.',
        subtitle: t('pages.agents.heroSubtitle') || AGENTS_DISCOVERY_FALLBACK_SUBTITLE,
      };
    }
    const name = activeCategoryDetails?.name || activeCategory;
    const desc = (activeCategoryDetails?.description || '').trim();
    return {
      ...base,
      topline: t('pages.agents.browseAgents'),
      headline: name,
      subtitle: desc || AGENTS_DISCOVERY_FALLBACK_SUBTITLE,
    };
  }, [
    activeCategory,
    activeCategoryDetails,
    query,
    t,
  ]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (activeCategory === 'all') {
        await Promise.all([fetchAgentCategories(), ...(hasUser ? [fetchMyAgents()] : [])]);
      } else if (activeCategory === 'my-agents') {
        if (hasUser) await fetchMyAgents();
      } else {
        await fetchCategoryAgents(activeCategory);
      }
    } finally {
      setRefreshing(false);
    }
  }, [activeCategory, hasUser, fetchAgentCategories, fetchCategoryAgents, fetchMyAgents]);

  const onLoadMore = useCallback(async () => {
    if (isLoadingMore) return;
    if (!categoryHasMore) return;
    try {
      setIsLoadingMore(true);
      const next = (categoryPage || 1) + 1;
      await fetchCategoryAgents(activeCategory, next);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, categoryHasMore, categoryPage, activeCategory, fetchCategoryAgents]);

  const onLoadMoreMyAgents = useCallback(async () => {
    if (isLoadingMore) return;
    if (!myAgentsHasMore) return;
    try {
      setIsLoadingMore(true);
      const next = (myAgentsPage || 1) + 1;
      await fetchMyAgents(next);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, myAgentsHasMore, myAgentsPage, fetchMyAgents]);

  const handleCopyAllAgentLinks = useCallback(async () => {
    try {
      await ensureAllMyAgents();
    } catch (error) {
      console.error('Failed to load all agent links:', error);
      addToast(t('pages.agents.linksCopyIncomplete'), 'error');
      return;
    }
    const ids = useAgentStore.getState().myAgentIds || [];
    const listed = [];
    for (const id of ids) {
      const agent = getAgentById(id);
      if (agent) listed.push(agent);
    }
    await copyAgentRegistry(sortAgents(listed, agentSort));
  }, [ensureAllMyAgents, getAgentById, copyAgentRegistry, agentSort, addToast, t]);

  // ── grid / responsiveness ─────────────────────────────────────────────
  // Compact-card grid drops a column at every breakpoint so cards keep
  // a comfortable ~260–340px each:
  //   ≤767  XS  → 1   (rail collapses to pills)
  //   ≤1160 MD  → 2   (rail visible, leaves ~520–940px for grid)
  //   ≤1499 LG  → 3
  //   ≥1500 XL  → 4   (max-content widens to fit, see styles)
  const gridCols = isXS ? 1 : isMD ? 2 : isXL ? 4 : 3;
  const cardWidth = Platform.OS === 'web'
    ? `calc((100% - ${(gridCols - 1) * 12}px) / ${gridCols})`
    : `${100 / gridCols}%`;
  // Use the same compact density for discovery, personal, and category grids.
  const tileCols = gridCols;
  const tileWidth = Platform.OS === 'web'
    ? `calc((100% - ${(tileCols - 1) * TILE_GAP}px) / ${tileCols})`
    : `${100 / tileCols}%`;

  // ── SEO structured data — preserved from the previous page ─────────────
  const seoStructuredData = useMemo(() => {
    const seenIds = new Set();
    const allAgents = [];
    (categoriesWithAgentIds || []).forEach((cat) => {
      (cat?.agentIds || []).forEach((id) => {
        if (id && !seenIds.has(id)) {
          const agent = getAgentById(id);
          if (agent?.id) {
            seenIds.add(id);
            allAgents.push(agent);
          }
        }
      });
    });
    const itemList = allAgents.slice(0, 100).map((agent, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: agent.slug
        ? `https://gab.ai/agents/${encodeURIComponent(agent.slug)}`
        : `https://gab.ai/g/${agent.id}`,
      name: agent.name,
    }));
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'AI Bots — Specialized AI Assistants',
          description: 'Browse Gab AI\u2019s catalog of specialized AI bots — coding, writing, research, finance, faith, productivity, and more. Pick a bot and start chatting.',
          url: 'https://gab.ai/agents',
          isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: 'https://gab.ai' },
          inLanguage: 'en-US',
          mainEntity: { '@type': 'ItemList', itemListElement: itemList },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://gab.ai' },
            { '@type': 'ListItem', position: 2, name: 'AI Bots', item: 'https://gab.ai/agents' },
          ],
        },
      ],
    };
  }, [categoriesWithAgentIds, getAgentById]);

  // ── loading / error gating for the editorial header view ──────────────
  const isInitialAllLoading =
    activeCategory === 'all' &&
    !isSearching &&
    isLoadingAgentCategories &&
    flatAgents.length === 0;

  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  const categoryChipItems = useMemo(() => {
    const items = [
      { id: 'all', label: t('pages.common.all') || 'All', icon: 'grid-2' },
    ];
    if (hasUser) {
      items.push({
        id: 'my-agents',
        label: t('pages.agents.myAgents'),
        icon: 'lock',
        iconSolid: true,
      });
    }
    categoryList.forEach((c) => {
      items.push({ id: c.slug, label: c.name });
    });
    return items;
  }, [t, hasUser, categoryList]);

  const agentsBrowseToolbarBase = useMemo(() => ({
    viewMode: catalogView,
    listMode: CATALOG_VIEW_MODES.LIST,
    gridMode: CATALOG_VIEW_MODES.GRID,
    onViewModeChange: setCatalogView,
    hideViewToggle: isEditorialAll,
    listA11yLabel: t('pages.agents.listView') || 'List view',
    gridA11yLabel: t('pages.agents.gridView') || 'Grid view',
    trailing: (
      <CatalogSortDropdown
        value={agentSort}
        options={agentSortOptions}
        onChange={setAgentSort}
        label={t('pages.models.sortLabel') || 'Sort'}
      />
    ),
  }), [catalogView, t, agentSort, agentSortOptions, isEditorialAll]);

  const agentsMobileToolbar = useMemo(() => (
    <CatalogMobileToolbar
      viewMode={catalogView}
      onViewModeChange={setCatalogView}
      listMode={CATALOG_VIEW_MODES.LIST}
      gridMode={CATALOG_VIEW_MODES.GRID}
      showViewToggle={!isEditorialAll}
      chipSlots={[
        <CatalogSortDropdown
          key="agents-sort"
          value={agentSort}
          options={agentSortOptions}
          onChange={setAgentSort}
          label={t('pages.models.sortLabel') || 'Sort'}
        />,
      ]}
    />
  ), [
    catalogView,
    isEditorialAll,
    t,
    agentSort,
    agentSortOptions,
  ]);

  // ── content branches ──────────────────────────────────────────────────
  const renderSectionGrid = (agents, { showLoading = false } = {}) => {
    const isList = catalogView === CATALOG_VIEW_MODES.LIST;
    const decorated = (agents || []).map(decorateAgent);

    if (showLoading && isList) {
      if (!isXS) {
        return (
          <View>
            <View style={styles.listHeader}>
              {AGENT_LIST_COLS.map((c) => (
                <View
                  key={c.id}
                  style={[listColumnStyle(c), { justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' }]}
                >
                  <Text size="xxs" weight="bold" color="tertiary" style={styles.listHeaderLabel}>
                    {translateAgentColumnLabel(c.id, t)}
                  </Text>
                </View>
              ))}
            </View>
            <View>
              {Array.from({ length: SECTION_PREVIEW }).map((_, i) => (
                <AgentListRow key={`lsk-${i}`} isLoading isXS={false} />
              ))}
            </View>
          </View>
        );
      }
      return (
        <View style={styles.listScrollMobile}>
          {Array.from({ length: SECTION_PREVIEW }).map((_, i) => (
            <AgentListRow key={`lskm-${i}`} isLoading isXS />
          ))}
        </View>
      );
    }

    if (showLoading) {
      return (
        <View style={styles.grid}>
          {Array.from({ length: SECTION_PREVIEW }).map((_, i) => (
            <View key={`s${i}`} style={[styles.gridCell, { width: cardWidth }]}>
              <AgentCard isLoading fillHeight={!isXS} />
            </View>
          ))}
        </View>
      );
    }

    if (isList) {
      if (!decorated.length) return null;
      if (isXS) {
        return (
          <View style={styles.listScrollMobile}>
            {decorated.map((a) => (
              <AgentListRow
                key={a.id}
                agent={a}
                isXS
                onPress={openAgentChat}
                onUseAgent={giveAgentJob}
              />
            ))}
          </View>
        );
      }
      return (
        <View>
          <View style={styles.listHeader}>
            {AGENT_LIST_COLS.map((c) => (
              <View
                key={c.id}
                style={[listColumnStyle(c), { justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' }]}
              >
                <Text size="xxs" weight="bold" color="tertiary" style={styles.listHeaderLabel}>
                  {translateAgentColumnLabel(c.id, t)}
                </Text>
              </View>
            ))}
          </View>
          <View style={{ paddingBottom: 8 }}>
            {decorated.map((a) => (
              <AgentListRow
                key={a.id}
                agent={a}
                isXS={false}
                onPress={openAgentChat}
                onUseAgent={giveAgentJob}
              />
            ))}
          </View>
        </View>
      );
    }

    return (
      <View style={styles.grid}>
        {decorated.map((a) => (
          <View key={a.id} style={[styles.gridCell, { width: cardWidth }]}>
            <AgentCard
              agent={a}
              onPress={openAgentChat}
              onUseAgent={giveAgentJob}
              fillHeight={!isXS}
            />
          </View>
        ))}
      </View>
    );
  };

  const renderSearchView = () => {
    if (searchError) {
      return (
        <StatusContainer
          type="error"
          message={searchError || t('pages.agents.errorLoading')}
          actionText={t('pages.common.retry')}
          onAction={() => searchAgents(query)}
        />
      );
    }
    return (
      <View style={{ gap: 10 }}>
        <View style={styles.sectionHeader}>
          <Text size="m" weight="bold">{t('pages.common.searchResults')}</Text>
          {searchResults.length > 0 && (
            <Text size="xxs" color="tertiary" weight="semibold">
              {t('pages.agents.featuredCount').replace('%COUNT%', String(searchResults.length))
                .replace('hand-picked', '')
                .trim()
                || `${searchResults.length}`}
            </Text>
          )}
        </View>
        {(isLoadingAgentSearch && searchResults.length === 0)
          ? renderSectionGrid([], { showLoading: true })
          : searchResults.length === 0
            ? (
              <StatusContainer
                type="empty"
                message={t('pages.agents.noAgentsFound').replace('%QUERY%', debouncedQuery || query)}
              />
            )
            : renderSectionGrid(sortedSearchResults)}
      </View>
    );
  };

  const renderMyAgentsView = () => {
    // Logged-out visitors hitting `/agents?category=my-agents` via a direct
    // link get a sign-in CTA instead of an empty state — we never call
    // `fetchMyAgents` for them.
    if (!hasUser) {
      return (
        <StatusContainer
          type="empty"
          message={t('pages.agents.signInForMyAgents') || 'Sign in to see and create your own agents.'}
          actionText={t('common.signUp') || 'Sign Up'}
          actionIcon="user-plus"
          onAction={() => requireAuth(() => {}, {
            source: 'agents_my_agents_empty',
            title: t('pages.agents.signInForMyAgents'),
            subtitle: t('pages.agents.intro.signInHint'),
            icon: 'robot',
          })}
        />
      );
    }
    if (myAgentsError) {
      return (
        <StatusContainer
          type="error"
          message={myAgentsError}
          actionText={t('pages.common.retry')}
          onAction={() => { if (hasUser) fetchMyAgents(); }}
        />
      );
    }
    return (
      <View style={{ gap: 10 }}>
        <View style={styles.sectionHeader}>
          <View>
            <Text size="m" weight="bold">{t('pages.agents.myAgents')}</Text>
            <Text size="xxs" color="tertiary" weight="semibold">
              {(() => {
                const listedCount = Number.isFinite(myAgentsTotal) && myAgentsTotal > 0
                  ? myAgentsTotal
                  : myAgents.length;
                return (listedCount === 1
                  ? t('pages.agents.agentCountSingle')
                  : t('pages.agents.agentCount')
                ).replace('%COUNT%', String(listedCount));
              })()}
            </Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {myAgents.length > 0 && (
              <Button
                text={t('pages.agents.copyAllLinks')}
                prependIcon="link"
                size="small"
                variant="secondary"
                onPress={handleCopyAllAgentLinks}
              />
            )}
            <Button
              text={t('pages.agents.createNew')}
              iconName="plus"
              size="small"
              onPress={onCreateAgent}
            />
          </View>
        </View>
        {isLoadingMyAgents && myAgents.length === 0
          ? renderSectionGrid([], { showLoading: true })
          : myAgents.length === 0
            ? (
              <StatusContainer
                type="empty"
                message={t('pages.agents.noAgentsYet')}
                actionText={t('pages.agents.createNew')}
                actionIcon="plus"
                onAction={onCreateAgent}
              />
            )
            : (
              <>
                {renderSectionGrid(sortedMyAgents)}
                {myAgentsHasMore && (
                  <LoadMoreButton
                    onPress={onLoadMoreMyAgents}
                    isLoading={isLoadingMore}
                    isLarge
                    style={{ marginTop: 12 }}
                  />
                )}
              </>
            )}
      </View>
    );
  };

  const renderCategoryView = () => {
    if (categoryError && categoryAgents.length === 0) {
      return (
        <StatusContainer
          type="error"
          message={categoryError || t('pages.agents.errorLoading')}
          actionText={t('pages.common.retry')}
          onAction={onRefresh}
        />
      );
    }

    return (
      <View style={{ gap: 10 }}>
        {isLoadingCategory && categoryAgents.length === 0
          ? renderSectionGrid([], { showLoading: true })
          : categoryAgents.length === 0
            ? (
              <StatusContainer
                type="empty"
                message={t('pages.agents.noAgentsInCategory')}
              />
            )
            : (
              <>
                {renderSectionGrid(sortedCategoryAgents)}
                {categoryHasMore && (
                  <LoadMoreButton
                    onPress={onLoadMore}
                    isLoading={isLoadingMore}
                    isLarge
                    style={{ marginTop: 12 }}
                  />
                )}
              </>
            )}
        {categorySlugProp && !isSearching && activeCategoryDetails && categoryMetaIntroText.length > 0 && (
          <View style={styles.categoryMetaIntro}>
            <Text size="xs" weight="bold" color="tertiary" style={{ letterSpacing: 0.8 }}>
              {t('pages.agents.categoryMetaEyebrow').toUpperCase()}
            </Text>
            <Text size="n" color="secondary" style={{ lineHeight: 22 }}>
              {categoryMetaIntroText}
            </Text>
          </View>
        )}
        {categorySlugProp && !isSearching && activeCategoryDetails && categoryPageCopyBlocks.length > 0 && (
          <View style={styles.pageCopyWrap}>
            <ToolPageCopy
              blocks={categoryPageCopyBlocks}
              palette={categoryPageCopyPalette}
              isMD={isMD || isSM}
            />
          </View>
        )}
      </View>
    );
  };

  const renderAllView = () => {
    if (categoriesError && flatAgents.length === 0) {
      return (
        <StatusContainer
          type="error"
          message={categoriesError}
          actionText={t('pages.common.retry')}
          onAction={onRefresh}
        />
      );
    }
    // Two rows per category on wide layouts (the API ships six per category).
    const tilesPerSection = isXS ? XS_SECTION_TILES : tileCols * 2;

    const renderTileGrid = (agents, { showLoading = false } = {}) => (
      <View style={styles.tileGrid}>
        {showLoading
          ? Array.from({ length: tilesPerSection }).map((_, i) => (
            <View key={`tsk-${i}`} style={[styles.tileCell, { width: tileWidth }]}>
              <AgentTile isLoading />
            </View>
          ))
          : agents.map((a) => (
            <View key={a.id} style={[styles.tileCell, { width: tileWidth }]}>
              <AgentTile agent={a} onPress={openAgentChat} onUseAgent={giveAgentJob} />
            </View>
          ))}
      </View>
    );

    const renderSectionHeader = ({ title, count, targetId, icon }) => {
      const countLabel = count == null
        ? null
        : (count === 1 ? t('pages.agents.agentCountSingle') : t('pages.agents.agentCount'))
          .replace('%COUNT%', String(count));
      return (
        <View style={styles.directoryHeader}>
          <View style={styles.directoryTitle}>
            <View style={styles.directoryTitleRow}>
              {icon ? <Icon name={icon} solid size={15} color={colors.text_color_primary} /> : null}
              <Text tag="h2" size="xl" weight="extraBold" numberOfLines={1} style={styles.directoryTitleText}>
                {title}
              </Text>
            </View>
            {countLabel ? (
              <Text size="n" color="secondary" style={styles.directoryCount}>{countLabel}</Text>
            ) : null}
          </View>
          <Pressable
            onPress={() => onChangeCategory(targetId)}
            accessibilityRole="link"
            accessibilityLabel={`${t('pages.agents.seeAll')} ${title}`}
            style={({ hovered }) => [
              styles.seeAll,
              { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block },
              hovered && Platform.OS === 'web' && {
                backgroundColor: colors.solid_color_tertiary,
                borderColor: colors.border_color_primary,
              },
            ]}
          >
            <Text size="n" weight="semibold">{t('pages.agents.seeAll')}</Text>
            <Icon name="arrow-right" size={11} color={colors.text_color_primary} />
          </Pressable>
        </View>
      );
    };

    if (isInitialAllLoading) {
      return (
        <View style={styles.directory}>
          {Array.from({ length: 4 }).map((_, i) => (
            <View key={i} style={[styles.directorySection, i > 0 && styles.directorySectionDivider]}>
              <View style={styles.directoryHeader}>
                <View style={{ gap: 8 }}>
                  <View style={[styles.skel, { width: 180, height: 22 }]} />
                  <View style={[styles.skel, { width: 80, height: 14 }]} />
                </View>
                <View style={[styles.skel, { width: 84, height: 34, borderRadius: 10 }]} />
              </View>
              {renderTileGrid([], { showLoading: true })}
            </View>
          ))}
        </View>
      );
    }

    return (
      <View style={styles.directory}>
        {sortedSectionsToRender.map((sec, idx) => (
          <View
            key={sec.slug}
            style={[styles.directorySection, idx > 0 && styles.directorySectionDivider]}
          >
            {renderSectionHeader({ title: sec.name, count: sec.total, targetId: sec.slug })}
            {renderTileGrid(sec.agents.slice(0, tilesPerSection))}
          </View>
        ))}

        {sortedSectionsToRender.length === 0 && (
          <Text size="s" color="tertiary" style={{ padding: 24, textAlign: 'center' }}>
            {t('pages.agents.noResults')}
          </Text>
        )}

        {!hasUser && landingCatalogPageCopyBlocks.length > 0 && (
          <View style={styles.pageCopyWrap}>
            <ToolPageCopy
              blocks={landingCatalogPageCopyBlocks}
              palette={landingCatalogPageCopyPalette}
              isMD={isMD || isSM}
            />
          </View>
        )}
      </View>
    );
  };

  const renderBody = () => {
    if (isSearching) return renderSearchView();
    if (activeCategory === 'my-agents') return renderMyAgentsView();
    if (activeCategory !== 'all') return renderCategoryView();
    return renderAllView();
  };

  // ── render ────────────────────────────────────────────────────────────
  return (
    <View style={styles.container}>
      {/* The category SSR wrapper renders its own per-category MetaHead;
          skip ours so we don't emit duplicate <head> tags. */}
      {!categorySlugProp && (
        <MetaHead
          title={t('pages.agents.title') || 'AI Bots — Specialized AI Assistants'}
          description="Browse Gab AI's catalog of specialized AI bots for coding, writing, research, finance, faith, productivity, and more. Pick a bot and start a conversation."
          canonical="/agents"
          keywords="AI agents, AI assistants, specialized AI, custom AI, AI characters, Gab AI agents, AI chat"
          structuredData={seoStructuredData}
        />
      )}

      <ScrollView
        ref={scrollViewRef}
        style={{ flex: 1 }}
        contentContainerStyle={styles.pageScrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={(e) => {
          scrollYRef.current = e.nativeEvent.contentOffset?.y ?? 0;
        }}
        scrollEventThrottle={16}
        keyboardShouldPersistTaps="handled"
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.text_color_secondary}
            colors={[colors.text_color_secondary]}
          />
        }
      >
        <View style={[styles.pageTop, { backgroundColor: colors.solid_color_primary }]}>
          <View style={styles.pageTopInner}>
            {showIntroHero ? (
              <AgentsIntroHero
                onCreate={onCreateAgent}
                onExplore={(y) => scrollViewRef.current?.scrollTo({ y, animated: true })}
                personalContent={(
                  <BotsPersonalSection
                    hasUser={hasUser}
                    count={Number.isFinite(myAgentsTotal) && myAgentsTotal > 0 ? myAgentsTotal : myAgents.length}
                    isLoading={isLoadingMyAgents && myAgents.length === 0}
                    error={myAgentsError}
                    onRetry={() => fetchMyAgents()}
                    onCreate={onCreateAgent}
                    onSeeAll={() => onChangeCategory('my-agents')}
                  >
                    <View style={styles.tileGrid}>
                      {isLoadingMyAgents && myAgents.length === 0
                        ? Array.from({ length: tileCols }).map((_, index) => (
                          <View key={`personal-skeleton-${index}`} style={[styles.tileCell, { width: tileWidth }]}>
                            <AgentTile isLoading />
                          </View>
                        ))
                        : sortedMyAgents.slice(0, tileCols * 2).map((agent) => (
                          <View key={agent.id} style={[styles.tileCell, { width: tileWidth }]}>
                            <AgentTile agent={agent} onPress={openAgentChat} onUseAgent={giveAgentJob} />
                          </View>
                        ))}
                    </View>
                  </BotsPersonalSection>
                )}
                search={{
                  value: query,
                  onChangeText: setQuery,
                  placeholder: t('pages.agents.searchPlaceholder'),
                }}
              >
                <AgentCategoryChips
                  items={categoryChipItems}
                  activeId={activeCategory}
                  onChange={onChangeCategory}
                  padX={heroPadX}
                />
              </AgentsIntroHero>
            ) : showAgentsDiscoveryHero ? (
              <CatalogDiscoveryHero {...agentsDiscoveryHeroProps} />
            ) : (
              <View style={[styles.searchWrap, { paddingHorizontal: heroPadX }]}>
                <View style={[styles.search, { borderColor: colors.border_color_secondary }]}>
                  <Icon name="search" size={14} color={colors.text_color_secondary} />
                  <TextInput
                    value={query}
                    onChangeText={setQuery}
                    placeholder={t('pages.agents.searchPlaceholder')}
                    placeholderTextColor={colors.text_color_tertiary}
                    style={styles.searchInput}
                    autoCorrect={false}
                    autoCapitalize="none"
                    accessibilityLabel={t('pages.agents.searchPlaceholder')}
                    {...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {})}
                  />
                  {query.length > 0 && (
                    <Pressable
                      onPress={() => setQuery('')}
                      hitSlop={6}
                      accessibilityRole="button"
                      accessibilityLabel={t('common.clear') || 'Clear search'}
                    >
                      <Icon name="xmark" size={13} color={colors.text_color_tertiary} />
                    </Pressable>
                  )}
                </View>
              </View>
            )}
            {!showIntroHero ? (
              <View style={[
                styles.chipsWrap,
                { paddingHorizontal: heroPadX, marginTop: showAgentsDiscoveryHero ? 8 : 0 },
              ]}
              >
                <AgentCategoryChips
                  items={categoryChipItems}
                  activeId={activeCategory}
                  onChange={onChangeCategory}
                  padX={heroPadX}
                />
              </View>
            ) : (
              <View style={{ height: isXS ? 16 : 20 }} />
            )}
          </View>
        </View>

        <CatalogBrowseShell
          isXS={isXS}
          splitGap={0}
          bodyGap={16}
          hideRail
          suppressMobilePills
          toolbar={isEditorialAll
            ? undefined
            : (isXS
              ? { ...agentsBrowseToolbarBase, mobile: agentsMobileToolbar }
              : agentsBrowseToolbarBase)}
        >
          {renderBody()}
        </CatalogBrowseShell>

        <View style={styles.footerWrap}>
          <AppDashboardFooter />
        </View>
      </ScrollView>
    </View>
  );
});

// ---------------------------------------------------------------------------
// Route entry — kept thin so the wrapper at /agents/category/<slug>.jsx
// can import the AgentsPage component directly without round-tripping
// through this default export.
// ---------------------------------------------------------------------------
function AgentsRoute() {
  return <AgentsPage />;
}

export default React.memo(AgentsRoute);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const createStyles = (colors, isXS) => StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: colors.solid_color_primary,
  },
  /** ScrollView content: hero + shell (matches /tools, /tasks). */
  pageScrollContent: {
    flexGrow: 1,
    width: '100%',
    paddingBottom: 24,
  },
  /** Hero + mobile pills strip at top of the page. */
  pageTop: {
    width: '100%',
    flexShrink: 0,
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
  },
  pageTopInner: {
    paddingBottom: 4,
    width: '100%',
    alignSelf: 'center',
  },
  searchWrap: {
    paddingTop: isXS ? 8 : 4,
    paddingBottom: isXS ? 8 : 10,
    gap: 10,
  },
  chipsWrap: {
    width: '100%',
    paddingBottom: 12,
  },
  pageCopyWrap: {
    marginTop: 8,
    paddingTop: 20,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border_color_secondary,
  },
  footerWrap: {
    paddingHorizontal: isXS ? 16 : 28,
  },
  categoryMetaIntro: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border_color_secondary,
    gap: 8,
  },
  search: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    height: isXS ? 40 : 44,
    borderRadius: 10,
    borderWidth: 1,
    backgroundColor: colors.solid_color_block,
  },
  searchInput: {
    flex: 1, color: colors.text_color_primary, fontSize: 14,
    ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  /** All view: stacked category sections separated by hairlines (no rails). */
  directory: {
    // The editorial All view has no toolbar, so it supplies its own top
    // breathing room below the hero divider.
    paddingTop: isXS ? 20 : 28,
  },
  directorySection: {
    gap: 16,
    paddingBottom: 24,
  },
  directorySectionDivider: {
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
  directoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 4,
  },
  directoryTitle: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  directoryTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },
  directoryTitleText: {
    letterSpacing: -0.4,
    lineHeight: 26,
    flexShrink: 1,
  },
  directoryCount: {
    fontVariant: ['tabular-nums'],
  },
  seeAll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 36,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    flexShrink: 0,
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'background-color 120ms ease, border-color 120ms ease' } : {}),
  },
  tileGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Platform.OS === 'web' ? TILE_GAP : 0,
    rowGap: TILE_GAP,
    marginHorizontal: Platform.OS === 'web' ? 0 : -6,
    alignItems: 'stretch',
  },
  tileCell: {
    paddingHorizontal: Platform.OS === 'web' ? 0 : 6,
    minWidth: 0,
    flexShrink: 0,
    ...(Platform.OS === 'web' ? { display: 'flex', flexDirection: 'column' } : {}),
  },
  grid: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 12,
    // `alignContent: flex-start` keeps wrapped lines packed at the top so a
    // sparse grid (e.g. a single "My Agents" card) doesn't let the lone line
    // stretch to fill leftover vertical space. On single-column (XS) layouts
    // the cell aligns to `flex-start` so the card stays content-height instead
    // of ballooning to the full viewport on native; multi-column keeps
    // `stretch` for equal-height rows + bottom-docked CTAs.
    alignContent: 'flex-start',
    alignItems: isXS ? 'flex-start' : 'stretch',
  },
  gridCell: {
    minWidth: 0,
    flexShrink: 0,
    alignSelf: isXS ? 'flex-start' : 'stretch',
    ...(Platform.OS === 'web' ? { display: 'flex', flexDirection: 'column' } : {}),
  },
  listHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 32,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
    ...(Platform.OS === 'web' && { position: 'sticky', top: 0, zIndex: 1 }),
  },
  listHeaderLabel: {
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  listScrollMobile: {
    padding: 12,
    gap: 8,
  },
  skel: {
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 4,
  },
});
